/*
  JILZARAH — photo viewer ("lightbox") behaviour (COMPONENT layer; built 2026-09-04)

  Opens any [data-jz-lightbox] trigger into a full-screen viewer where the photo can be seen
  up close: pinch or double-tap to zoom on touch, scroll-wheel or double-click on desktop,
  drag to pan while zoomed, swipe or arrow keys to move between photos, Esc or the × to close.
  Built because field testing on the Halloween page (2026-09-04) showed people tapping product
  photos expecting to see more, and the page had no way to look closer or see the gallery.

  Two ways to describe the photos:
    1. data-jz-lb-items='[{"src","w","h","alt","caption"}, …]' on the trigger (self-contained;
       used by the Halloween order portraits: one tap shows that product's whole gallery), with
       data-jz-lb-start="<index>" optional.
    2. data-jz-lightbox="<group>" + data-jz-lb-src / -w / -h / -alt / -caption on each trigger:
       every trigger with the same group name is one gallery, in DOM order (the gallery section).

  The viewer DOM is built once per page and reused. No dependencies. Vanilla DOM only.
  Styles: assets/jilzarah-lightbox.css   Loader: snippets/jilzarah-lightbox.liquid
*/
(function () {
  'use strict';
  if (window.__jzLightbox) return;
  window.__jzLightbox = true;

  var MAX = 4, DOUBLE = 2.6;
  var isTouch = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var root, stage, img, cap, count, hint, btnPrev, btnNext, btnClose;
  var items = [], index = 0, opener = null;
  var scale = 1, tx = 0, ty = 0;
  var pointers = {}, pinch = null, drag = null, lastTap = 0;
  var base = { w: 0, h: 0 }, lastMoved = 0, touchZoomAt = 0; /* the photo's on-screen size at scale 1 */

  function build() {
    if (root) return;
    root = document.createElement('div');
    root.className = 'jz-lb';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Photo viewer');
    root.hidden = true;
    root.innerHTML =
      '<button class="jz-lb__close" type="button" aria-label="Close">&times;</button>' +
      '<button class="jz-lb__nav jz-lb__nav--prev" type="button" aria-label="Previous photo">&#8249;</button>' +
      '<button class="jz-lb__nav jz-lb__nav--next" type="button" aria-label="Next photo">&#8250;</button>' +
      '<div class="jz-lb__stage"><img class="jz-lb__img" alt="" draggable="false"></div>' +
      '<div class="jz-lb__bar"><span class="jz-lb__caption"></span><span class="jz-lb__count"></span><span class="jz-lb__hint"></span></div>';
    document.body.appendChild(root);
    stage = root.querySelector('.jz-lb__stage');
    img = root.querySelector('.jz-lb__img');
    cap = root.querySelector('.jz-lb__caption');
    count = root.querySelector('.jz-lb__count');
    hint = root.querySelector('.jz-lb__hint');
    btnPrev = root.querySelector('.jz-lb__nav--prev');
    btnNext = root.querySelector('.jz-lb__nav--next');
    btnClose = root.querySelector('.jz-lb__close');

    btnClose.addEventListener('click', close);
    btnPrev.addEventListener('click', function () { go(-1); });
    btnNext.addEventListener('click', function () { go(1); });
    /* a click on the dark area closes; a drag or swipe that ends there does not */
    root.addEventListener('click', function (ev) { if (ev.target === root || (ev.target === stage && lastMoved < 10)) close(); });

    stage.addEventListener('pointerdown', onDown);
    stage.addEventListener('pointermove', onMove);
    stage.addEventListener('pointerup', onUp);
    stage.addEventListener('pointercancel', onUp);
    stage.addEventListener('wheel', onWheel, { passive: false });
    /* the browser also synthesises a dblclick (a MouseEvent, no pointerType) from a double-tap,
       which would undo the zoom the tap just made: ignore it right after a touch double-tap */
    stage.addEventListener('dblclick', function (ev) { if (Date.now() - touchZoomAt > 600) toggleZoom(ev.clientX, ev.clientY); });
    document.addEventListener('keydown', function (ev) {
      if (root.hidden) return;
      if (ev.key === 'Escape') close();
      else if (ev.key === 'ArrowLeft') go(-1);
      else if (ev.key === 'ArrowRight') go(1);
      else if (ev.key === '+' || ev.key === '=') zoomTo(Math.min(MAX, scale * 1.4));
      else if (ev.key === '-') zoomTo(Math.max(1, scale / 1.4));
    });
  }

  /* ---------- geometry ---------- */
  function apply(animate) {
    img.style.transition = (animate && !reduced) ? 'transform 180ms ease-out' : 'none';
    img.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) scale(' + scale + ')';
    stage.classList.toggle('is-zoomed', scale > 1.01);
  }
  function clamp() {
    /* keep the zoomed photo covering the stage: never drag it fully out of view */
    var s = stage.getBoundingClientRect();
    var w = base.w * scale, h = base.h * scale;
    var maxX = Math.max(0, (w - s.width) / 2), maxY = Math.max(0, (h - s.height) / 2);
    tx = Math.max(-maxX, Math.min(maxX, tx));
    ty = Math.max(-maxY, Math.min(maxY, ty));
  }
  function zoomTo(next, cx, cy, animate) {
    /* zoom around a screen point so what is under the finger stays under the finger */
    var s = stage.getBoundingClientRect();
    var px = (cx == null ? s.left + s.width / 2 : cx) - (s.left + s.width / 2);
    var py = (cy == null ? s.top + s.height / 2 : cy) - (s.top + s.height / 2);
    var k = next / scale;
    tx = px - (px - tx) * k;
    ty = py - (py - ty) * k;
    scale = next;
    if (scale <= 1.001) { scale = 1; tx = 0; ty = 0; } else { clamp(); }
    apply(animate !== false);
  }
  function toggleZoom(cx, cy) { zoomTo(scale > 1.01 ? 1 : DOUBLE, cx, cy, true); }
  function reset() { scale = 1; tx = 0; ty = 0; apply(false); }

  /* ---------- pointers: pan, pinch, swipe, double-tap ---------- */
  function onDown(ev) {
    if (ev.button && ev.button !== 0) return;
    stage.setPointerCapture && stage.setPointerCapture(ev.pointerId);
    pointers[ev.pointerId] = { x: ev.clientX, y: ev.clientY };
    var ids = Object.keys(pointers);
    if (ids.length === 2) {
      var a = pointers[ids[0]], b = pointers[ids[1]];
      pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: scale };
      drag = null;
    } else if (ids.length === 1) {
      drag = { x: ev.clientX, y: ev.clientY, tx: tx, ty: ty, moved: 0, t: Date.now() };
    }
    ev.preventDefault();
  }
  function onMove(ev) {
    if (!pointers[ev.pointerId]) return;
    pointers[ev.pointerId] = { x: ev.clientX, y: ev.clientY };
    var ids = Object.keys(pointers);
    if (pinch && ids.length >= 2) {
      var a = pointers[ids[0]], b = pointers[ids[1]];
      var d = Math.hypot(a.x - b.x, a.y - b.y);
      var next = Math.max(1, Math.min(MAX, pinch.scale * (d / pinch.dist)));
      zoomTo(next, (a.x + b.x) / 2, (a.y + b.y) / 2, false);
      return;
    }
    if (!drag) return;
    var dx = ev.clientX - drag.x, dy = ev.clientY - drag.y;
    drag.moved = Math.max(drag.moved, Math.abs(dx) + Math.abs(dy));
    if (scale > 1.01) {
      tx = drag.tx + dx; ty = drag.ty + dy; clamp(); apply(false);
    } else {
      /* not zoomed: a horizontal drag previews the swipe */
      img.style.transition = 'none';
      img.style.transform = 'translate(' + dx + 'px, 0)';
    }
  }
  function onUp(ev) {
    delete pointers[ev.pointerId];
    var ids = Object.keys(pointers);
    if (ids.length < 2) pinch = null;
    if (ids.length === 1) { var p = pointers[ids[0]]; drag = { x: p.x, y: p.y, tx: tx, ty: ty, moved: 99, t: 0 }; return; }
    if (!drag) return;
    var d = drag; drag = null;
    var dx = ev.clientX - d.x, dy = ev.clientY - d.y;
    lastMoved = d.moved;
    if (scale <= 1.01) {
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { go(dx < 0 ? 1 : -1); return; }
      apply(true);
    }
    if (d.moved < 10 && ev.pointerType === 'touch') {
      var now = Date.now();
      if (now - lastTap < 320) { touchZoomAt = now; toggleZoom(ev.clientX, ev.clientY); lastTap = 0; }
      else lastTap = now;
    }
  }
  function onWheel(ev) {
    ev.preventDefault();
    var next = Math.max(1, Math.min(MAX, scale * (ev.deltaY < 0 ? 1.15 : 1 / 1.15)));
    zoomTo(next, ev.clientX, ev.clientY, false);
  }

  /* ---------- open / navigate / close ---------- */
  function show(i, animate) {
    index = (i + items.length) % items.length;
    var it = items[index];
    reset();
    img.classList.add('is-loading');
    img.onload = function () { img.classList.remove('is-loading'); measure(); };
    img.src = it.src;
    if (it.w && it.h) { img.width = it.w; img.height = it.h; }
    img.alt = it.alt || '';
    cap.textContent = it.caption || it.alt || '';
    count.textContent = items.length > 1 ? (index + 1) + ' / ' + items.length : '';
    btnPrev.hidden = btnNext.hidden = items.length < 2;
    /* the hint promises more photos only when there are more (a one-photo gallery said "arrows for more"; seen 2026-10-30) */
    hint.textContent = (isTouch ? 'Pinch or double-tap to zoom' : 'Scroll or double-click to zoom') + (items.length > 1 ? (isTouch ? ' · swipe for more' : ' · arrows for more') : '');
    /* warm the neighbours so a swipe lands on a loaded photo */
    [index + 1, index - 1].forEach(function (n) { var nb = items[(n + items.length) % items.length]; if (nb && nb !== it) { var pre = new Image(); pre.src = nb.src; } });
    if (animate && !reduced) { img.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 160 }); }
  }
  function measure() { var r = img.getBoundingClientRect(); base = { w: r.width / scale, h: r.height / scale }; }
  window.addEventListener('resize', function () { if (root && !root.hidden) { reset(); measure(); } });
  function go(step) { if (items.length > 1) show(index + step, true); }
  function open(list, start, trigger) {
    build();
    if (!list.length) return;
    items = list; opener = trigger || null;
    /* take the accent from wherever the viewer was opened, so the arrows match that page */
    var cs = trigger ? getComputedStyle(trigger) : null;
    var accent = cs && cs.getPropertyValue('--jz-lb-accent').trim(), ink = cs && cs.getPropertyValue('--jz-lb-accent-ink').trim();
    if (accent) root.style.setProperty('--jz-lb-accent', accent); else root.style.removeProperty('--jz-lb-accent');
    if (ink) root.style.setProperty('--jz-lb-accent-ink', ink); else root.style.removeProperty('--jz-lb-accent-ink');
    root.hidden = false;
    document.documentElement.classList.add('jz-lb-open');
    show(start || 0, false);
    btnClose.focus({ preventScroll: true });
  }
  function close() {
    if (!root || root.hidden) return;
    root.hidden = true;
    document.documentElement.classList.remove('jz-lb-open');
    img.removeAttribute('src');
    pointers = {}; pinch = null; drag = null;
    if (opener && opener.focus) opener.focus({ preventScroll: true });
  }

  /* ---------- triggers ---------- */
  function itemFrom(el) {
    return { src: el.dataset.jzLbSrc, w: +el.dataset.jzLbW || 0, h: +el.dataset.jzLbH || 0, alt: el.dataset.jzLbAlt || '', caption: el.dataset.jzLbCaption || '' };
  }
  document.addEventListener('click', function (ev) {
    var t = ev.target.closest('[data-jz-lightbox]');
    if (!t) return;
    ev.preventDefault();
    var list, start = 0;
    if (t.dataset.jzLbItems) {
      try { list = JSON.parse(t.dataset.jzLbItems); } catch (e) { return; }
      start = +t.dataset.jzLbStart || 0;
    } else {
      var group = t.dataset.jzLightbox;
      var all = group ? document.querySelectorAll('[data-jz-lightbox="' + group + '"]') : [t];
      list = []; all.forEach(function (el, i) { if (el === t) start = i; list.push(itemFrom(el)); });
    }
    open(list, start, t);
  });

  window.jzLightbox = { open: open, close: close };
})();
